import {
  ui,
  Map,
  TileLayer,
  VectorLayer,
  Extent,
  LineString,
  Geometry,
  MapAnimationOptionsType,
  MapPaddingType,
  Properties,
  EventParams,
  animation,
  Marker,
} from "maptalks"
import TWEEN from "@tweenjs/tween.js"
import _ from "lodash"
import {
  feature as turfFeature,
  featureCollection,
  lineString,
  point,
  LineString as LineStringType,
  Feature,
} from "@turf/helpers"
import turfDistance from "@turf/distance"
import turfCenter from "@turf/center"
import bbox from "@turf/bbox"
import scale from "@turf/transform-scale"
import bboxPolygon from "@turf/bbox-polygon"

import { PerspectiveCamera, Color as ThreeColor } from "three"
import { BaseObject, ThreeLayer } from "maptalks.three"

import {
  LAYERS,
  LAYER_OPTIONS,
  LAYER_FEATURE_TYPE_OBJ,
  HIGHLIGHT_LAYER_NAME,
  USER_LOCATION_LAYER_NAME,
  ORIGIN_MARKER_ID,
  DESTINATION_MARKER_ID,
  DEFAULT_HIGHLIGHT_OPTIONS,
  DEFAULT_SET_HIGHLIGHT_2DELEMENT_IDS_OPTIONS,
  USER_LOCATION_ELEMENT_ID,
  LAST_USER_LOCATION_ELEMENT_ID_PREFIX,
  ALWAYS_VISIBLE_FEATURE_TYPES,
  LOCALE_SYMBOL_KEY,
  DEFAULT_LOCALE,
  VENUE_EVENTS,
  POI_MARKER_LAYER_NAME,
  SUFFIX_MARKER,
  DEFAULT_EXTRUDE_CONFIGS,
  DEFAULT_BASEMAP,
  resolveBasemapConfig,
} from "./constants"
import { ISetHighlightElementIdsOptions } from "./interface"

import {
  styledFeatureGenerator,
  getExtrudeConfigByFeature,
  buildCompositeId,
  getMarkerId,
  computeAltitudeFromOrdinals,
} from "./utils/createElements"

import { getBearingBetweenPoints } from "./utils/math"

import {
  NavigationPath,
  Billboard,
  SpriteMarker,
  GroundLabel,
} from "./object3d"
import {
  createHighlighExtrudeObjectController,
  createHighlighBillboardController,
} from "./utils/createHighlightElement"
import { Coordinate, IMapConfig, IBasemapConfig } from "services/venue"
import config from "config"
import { getFeatureOrdinal } from "utils/feature"

const defaultOptions = {
  center: [100.5017051, 13.7572619],
  zoom: 18.5,
  bearing: 0,
  pixelRatio: 1,
  locale: DEFAULT_LOCALE,
}

const CLICK_TOLERANCE = 20
const MULTIORDINAL_HEIGHT_METER = 10

export class IndoorMap {
  //TODO: refac functions; let them do only 1 thing in a function

  /** Note: "#" means private variables */
  #defaultCenter = defaultOptions.center
  #defaultZoom = defaultOptions.zoom
  #defaultBearing = defaultOptions.bearing
  #defaultPitch
  #styler = null
  #featuresInitted = false
  #elementsLoaded = false
  #elements: Record<string, any> = {}
  #features = []
  #venues = []
  #venueInView: string | null = null
  #ordinals = []
  #mapTheme = {}
  #billboards = []
  #billboardObjects = []
  #spriteMarkerObjects = []
  #mapDecorations = []
  #groundLabels = []
  #groundObjects = []
  #navigationGeometries: Record<string, NavigationPath> = {}
  #venueObjects = []
  #venueBillboardObjects = []
  #glbObjects = []
  #objects = []
  #object3ds = []
  #isClicked = false
  #highlightElementIds = []
  #relatedHighlightHiddenElementIds = []
  #highlightObjectIds = []
  #hiddenElementIds = []
  #hiddenObjectIds = []
  #hiddenGeometryIds = []
  #highlightObjectControllers = []
  #userLocationGeometry = null
  #userLocationElement = null
  #mapConfig: IMapConfig = {}
  #basemap: IBasemapConfig = DEFAULT_BASEMAP
  #locale: string = defaultOptions.locale
  #lastUserLocationGeometries = []
  #lastUserLocationElements = []

  #zoomLevel = defaultOptions.zoom

  #isLayersFadingOnZoom = false

  #touchStartTarget = null
  #touchStartPoint = null

  #onClickElement = (e) => {}
  #animationsToRun = []

  map: Map | null = null
  showVenueObject = false
  threeLayer: ThreeLayer | null = null

  onMapReady = () => {}
  onMapLoading = () => {}
  onModelReady = () => {}

  constructor(elementId, options) {
    const {
      center,
      defaultZoom,
      onMapReady,
      onModelReady,
      onMapLoading,
      pixelRatio,
      locale,
    } = _.merge({}, defaultOptions, options)
    // To be moved to config
    this.#defaultZoom = defaultZoom
    this.#zoomLevel = defaultZoom

    this.map = new Map(elementId, {
      attribution: false,
      center,
      zoom: defaultZoom,
      clickTimeThreshold: 600,
      baseLayer: this.#createBaseLayer(DEFAULT_BASEMAP),
      layers: [],
    })
    this.locale = locale
    this.map.setDevicePixelRatio(pixelRatio)
    this.onMapReady = onMapReady
    this.onMapLoading = onMapLoading
    this.onModelReady = onModelReady
    this.showVenueObject = false
    // Bind events
    this.map.on("zooming", this.handleMapZooming)
    this.map.on("click", this.handleMapClick)
    this.map.on("resize", (e) => {
      const objectScale = _.clamp(this.getScaleByMapAspectRatio(), 1, 2)
      this.#venueBillboardObjects.forEach((object) => {
        object.getObject3d().scale.set(objectScale, objectScale, 1)
      })
    })
    this.map.on("animateinterrupted", () => {
      // When an animation is interrupted, cancel the previous player instance
      // to prevent errors (e.g., flyTo) caused by an unexpected undefined player.
      this.map._animPlayer?.cancel()
    })
  }

  #createBaseLayer(cfg: IBasemapConfig): TileLayer | null {
    const c = resolveBasemapConfig(cfg)
    if (c.enabled === false) return null

    const options: Record<string, any> = {
      urlTemplate: c.url_template,
      opacity: c.opacity,
      attribution: "",
      hitDetect: false,
      decodeImageInWorker: true,
      errorUrl: "/assets/img/tile-placeholder.png",
    }
    if (c.css_filter) options.cssFilter = c.css_filter
    if (c.subdomains?.length) options.subdomains = c.subdomains
    if (c.max_available_zoom > 0)
      options.maxAvailableZoom = c.max_available_zoom

    return new TileLayer("base", options)
  }

  /**
   * Events
   */

  handleMapZooming = ({ to }) => {
    this.#zoomLevel = to
  }

  handleMapClick = ({ coordinate }) => {
    const { x, y } = coordinate
    console.log(
      `[Coordinates]: x: ${_.round(x, 8)} y: ${_.round(
        y,
        8
      )}, [Bearing]: ${this.map.getBearing()}, [Pitch]: ${this.map.getPitch()}`
    )
  }

  getScaleByMapAspectRatio = () => {
    const { width, height } = this.map.getSize()
    return Math.round(parseFloat((width / height).toFixed(2)))
  }

  handleMapTouchEnd = ({ containerPoint }) => {
    const { x, y } = containerPoint
    const { x: touchStartX, y: touchStartY } = this.#touchStartPoint

    const dis = Math.hypot(x - touchStartX, y - touchStartY)
    this.map.off("touchend", this.handleMapTouchEnd)

    if (dis > 0 && dis < CLICK_TOLERANCE) {
      console.log("Forgiving click", this.#touchStartTarget.properties.id)
      this.handleClickElement({ target: this.#touchStartTarget })
    }

    this.#touchStartTarget = null
    this.#touchStartPoint = null
  }

  /**
   * Getters & Setters
   */
  get defaultBearing() {
    return this.#defaultBearing
  }

  get defaultPitch() {
    return this.#defaultPitch
  }

  get defaultCenter() {
    return this.#defaultCenter
  }

  get defaultZoom() {
    return this.#defaultZoom
  }

  get elementsLoaded() {
    return this.#elementsLoaded
  }

  get pixelRatio() {
    return this.map.getDevicePixelRatio()
  }

  get locale() {
    return this.#locale
  }

  set mapTheme(value) {
    this.#mapTheme = value
    this.#styler = styledFeatureGenerator(this.#mapTheme)
  }

  get venues() {
    return this.#venues || []
  }

  #findAndSetVenueInView = () => {
    const venueInViewWithDistance = this.findVenueInView()
    if (
      venueInViewWithDistance &&
      (!this.#venueInView ||
        venueInViewWithDistance.venueId !== this.#venueInView)
    ) {
      this.#venueInView = venueInViewWithDistance.venueId
      this.map.fire(VENUE_EVENTS.VENUE_MOVEINTOVIEW, venueInViewWithDistance)
    }
  }

  set detectVenueInView(value) {
    if (value) {
      // Set initial venueInView
      this.#findAndSetVenueInView()
      this.map.on("moveend", this.#findAndSetVenueInView)
    } else {
      this.map.off("moveend", this.#findAndSetVenueInView)
    }
  }

  get ordinals() {
    return this.#ordinals || []
  }

  set ordinals(value) {
    if (!Array.isArray(value)) throw new Error("ordinals must be Array")
    this.#ordinals = value
  }

  set billboards(value) {
    this.#billboards = value
  }

  set defaultCenter(value) {
    this.#defaultCenter = value
  }
  set defaultBearing(value) {
    this.#defaultBearing = value
  }
  set defaultPitch(value) {
    this.#defaultPitch = value
  }
  set defaultZoom(value) {
    this.#defaultZoom = value
  }
  set mapConfig(value: IMapConfig) {
    this.#mapConfig = value
  }

  set basemap(cfg: IBasemapConfig) {
    const resolved = resolveBasemapConfig(cfg)
    if (_.isEqual(resolved, this.#basemap)) return
    this.#basemap = resolved

    const layer = this.#createBaseLayer(resolved)
    if (layer) {
      this.map.setBaseLayer(layer)
    } else {
      this.map.removeBaseLayer()
    }
  }

  set mapDecorations(value) {
    this.#mapDecorations = value
  }

  set maxZoom(value: number) {
    this.map.setMaxZoom(value)
    const spatialReference = {
      projection: "EPSG:3857",
      resolutions: (function () {
        const resolutions = []
        const d = 2 * 6378137 * Math.PI
        for (let i = 0; i < value; i++) {
          resolutions[i] = d / (256 * Math.pow(2, i))
        }
        return resolutions
      })(),
    }
    this.map.setSpatialReference(spatialReference)
  }

  set minZoom(value: number) {
    this.map.setMinZoom(value)
  }

  set groundLabels(value) {
    this.#groundLabels = value
  }

  set pixelRatio(value: number) {
    this.map.setDevicePixelRatio(value)
  }

  set onClickElement(func) {
    this.#onClickElement = func
  }

  set locale(value: string) {
    this.#locale = value || defaultOptions.locale

    // Update elements affected by locale changes.
    this.updateUserLocationSymbolByLocale(this.#locale)
  }

  set features(value) {
    /**
     * Setup with features
     * -------------------
     * 1. Create ordinals
     * 2. Init Layers (based on ordinals)
     * 3. Create elements
     */
    if (value.length === 0 || !this.#mapTheme) {
      return
    }

    this.#features = value

    this.#venues = _(this.#features)
      .filter((f) => f.feature_type === "venue")
      .value()

    this.ordinals = _(this.#features)
      .filter((f) => f.feature_type === "level")
      .map((f) => f.properties.ordinal)
      .sort()
      .uniq()
      .value()

    if (typeof this.onMapLoading === "function") this.onMapLoading()

    if (this.#featuresInitted) {
      this.#clearElements()
      this.#createElements()
      return
    }
    this.#initMapElements()
  }

  /**
   * Private internal methods
   */

  #clearElements() {
    LAYERS.forEach((layerKey) => {
      this.#clearAllElementOnLayerByName(layerKey)
    })
    const scene = this.threeLayer.getScene()
    if (scene) {
      scene.children = scene.children.filter(
        (children) => children instanceof PerspectiveCamera
      )
    }
  }

  #createLayers() {
    const map = this.map
    // Clear Existing Layers
    if (!this.ordinals || this.ordinals.length === 0) return
    // Create Layers
    LAYERS.forEach((layerKey) => {
      const layerID = layerKey
      const option = LAYER_OPTIONS[layerKey]
      const layer = new VectorLayer(layerID, option)
      map.addLayer(layer)
    })

    // ThreeJS Layer
    const threeLayer = new ThreeLayer("t_building", {
      forceRenderOnMoving: true,
      forceRenderOnRotating: true,
    })
    this.threeLayer = threeLayer

    //@ts-ignore
    threeLayer.addTo(this.map)
  }
  #initMapElements() {
    this.#createLayers()
    const threeLayer = this.threeLayer
    if (threeLayer) {
      this.threeLayer.prepareToDraw = async (gl, scene, camera) => {
        await this.#createElements()
      }
    }
  }

  handleClickElement = (e: EventParams) => {
    if (this.#isClicked) return
    this.#isClicked = true
    const onClickElement = this.#onClickElement
    if (!_.isFunction(onClickElement)) return
    this.#onClickElement(e)
    this.#isClicked = false
  }

  setCenter(center: Coordinate) {
    this.map.setCenter(center)
  }

  async #createElements() {
    const {
      // 2D
      createVenue,
      createLevel,
      createUnit,
      createOpening,
      createSection,
      createSectionMarker,
      createFixture,
      createKiosk,
      createOccupant,
      createDecoration,
      // 3D
      create3DFootprint,
      create3DGroundLabel,
      create3DBillboard,
      create3DUnit,
      create3DKiosk,
      createVenue3DModel,
      createExtrudedUnit,
      createExtrudedSection,
      createExtrudedLevel,
      create3DFixture,
      createExtrudedKiosk,
      create3DAmenityMarker,
      create3DOccupantAmenityMarker,
      create3DOpeningMarker,
      createOccupantGroundLabel,
      // Light
      createAmbientLight,
      createDirectionalLight,

      create3DModels,
    } = this.#styler

    let elements = {}
    let object3ds = []

    const scene = this.threeLayer.getScene()
    if (scene) {
      const {
        ambientLight: ambientLightConfig = {},
        directionalLight: directionalLightConfig = {},
      } = _.get(this.#mapConfig, "light", {
        ambientLight: {},
        directionalLight: {},
      })

      const ambientLight = createAmbientLight(ambientLightConfig)
      scene.add(ambientLight)

      const light = createDirectionalLight(directionalLightConfig)
      scene.add(light)
    }
    for (const feature of this.#features) {
      try {
        const { feature_type: featureType, properties, id } = feature
        const layerName = _.get(
          LAYER_FEATURE_TYPE_OBJ,
          featureType,
          featureType
        )
        const layer = this.map.getLayer(layerName)
        let geometry
        const category = _.get(feature, "properties.category")

        const extrudeConfig = _.get(this.#mapConfig, "extrude")
        const mergedExtrudeConfig = _.merge(
          {},
          DEFAULT_EXTRUDE_CONFIGS,
          extrudeConfig
        )
        const textMarkerType = _.get(
          this.#mapConfig,
          "text_marker_type",
          "ui-marker"
        )

        const featureExtrudeConfig = getExtrudeConfigByFeature(
          mergedExtrudeConfig,
          feature
        )

        switch (featureType) {
          case "venue": {
            geometry = createVenue(feature).addTo(layer)
            // Add 3D Model
            const models = await createVenue3DModel(feature, this.threeLayer)
            models.forEach((model) => {
              model.on("click", this.handleClickElement)
              object3ds.push(model)
              this.#venueObjects.push(model)
            })

            //! The creation of venue logo markers is currently available only for the One Siam project
            try {
              const hasVenueLogoBillboard = !!feature?.properties?.marker
              // Add Venue Marker (Billboard)
              if (hasVenueLogoBillboard) {
                // NOTE: Temporary solution for creating billboards on 3D models.
                const displayPointGeometry = _.get(
                  feature,
                  "properties.display_point"
                )
                const coordinates = _.get(
                  feature,
                  "properties.marker_geometry_coordinates"
                )
                const logo = _.get(feature, "properties.marker")
                const properties = _.get(feature, "properties.marker_style", {})
                const venueBillboardFeature = {
                  id: `billboard-${feature.id}`,
                  feature_type: "billboard",
                  geometry: {
                    ...displayPointGeometry,
                    coordinates:
                      coordinates || displayPointGeometry?.coordinates,
                  },
                  properties: {
                    ...properties,
                    logo,
                  },
                }
                const venueMarker = create3DBillboard(
                  venueBillboardFeature,
                  this.threeLayer
                )
                const scale = _.clamp(this.getScaleByMapAspectRatio(), 1, 2)
                venueMarker.getObject3d().scale.set(scale, scale, 1)
                this.threeLayer.addMesh(venueMarker)
                this.#venueObjects.push(venueMarker)
                this.#venueBillboardObjects.push(venueMarker)
              }
            } catch (err) {
              console.log(
                "Error: Unable to create venue billboard marker.",
                err
              )
            }
            break
          }
          case "level": {
            const extrudedLevel = createExtrudedLevel(
              feature,
              this.threeLayer,
              featureExtrudeConfig
            )

            if (extrudedLevel) {
              extrudedLevel.on("click", this.handleClickElement)
              object3ds.push(extrudedLevel)
            } else {
              geometry = createLevel(feature).addTo(layer)
            }
            break
          }
          case "unit": {
            const models = await create3DUnit(feature, this.threeLayer)

            models.forEach((model) => {
              model.on("click", this.handleClickElement)
              object3ds.push(model)
              this.#glbObjects.push(model)
            })
            const locatedLevel = feature?.properties?.level
            const levelExtrudeConfig = getExtrudeConfigByFeature(
              mergedExtrudeConfig,
              locatedLevel
            )

            const levelHeight = levelExtrudeConfig?.height ?? 0
            const option = { ...featureExtrudeConfig, altitude: levelHeight }
            const hasAddedModel3d = models?.length > 0

            if (hasAddedModel3d || !featureExtrudeConfig) {
              geometry = createUnit(feature)
                ?.on("click", this.handleClickElement)
                .addTo(layer)
            } else {
              const extrudedUnit = createExtrudedUnit(
                feature,
                this.threeLayer,
                option
              )

              if (!extrudedUnit) break

              extrudedUnit.on("click", this.handleClickElement)

              // Forgiving Click
              extrudedUnit.on("touchstart", (e) => {
                this.#touchStartTarget = e.target
                this.#touchStartPoint = e.containerPoint
                this.map.on("touchend", this.handleMapTouchEnd)
              })

              object3ds.push(extrudedUnit)
            }
            break
          }
          case "amenity": {
            if (feature.properties.is_featured) {
              const billboardObj = create3DBillboard(feature, this.threeLayer)
              billboardObj?.on("click", this.handleClickElement)
              this.#billboardObjects.push(billboardObj)
              this.#spriteMarkerObjects.push(billboardObj)
              object3ds.push(billboardObj)
              break
            }
            const marker3d = create3DAmenityMarker(
              feature,
              this.threeLayer,
              extrudeConfig
            )
            marker3d?.on("click", this.handleClickElement)
            this.#spriteMarkerObjects.push(marker3d)
            object3ds.push(marker3d)
            break
          }
          case "opening": {
            switch (category) {
              case "emergencyexit":
                const { geometry } = turfCenter(feature)
                const markerFeature = {
                  ...feature,
                  geometry,
                }
                const marker3d = create3DOpeningMarker(
                  markerFeature,
                  this.threeLayer,
                  extrudeConfig
                )?.on("click", this.handleClickElement)
                object3ds.push(marker3d)
                break
              default:
            }
            geometry = createOpening(feature)
              ?.on("click", this.handleClickElement)
              .addTo(layer)
            break
          }
          case "section": {
            // If section feature flag is disabled, create a normal section on the map
            if (!config("sectionFeatureFlag")) {
              geometry = createSection(feature).addTo(layer)
              break
            }
            const locatedLevel = feature?.properties?.level
            const levelExtrudeConfig = getExtrudeConfigByFeature(
              mergedExtrudeConfig,
              locatedLevel
            )

            const levelHeight = levelExtrudeConfig?.height ?? 0
            const option = { ...featureExtrudeConfig, altitude: levelHeight }

            if (!featureExtrudeConfig) {
              geometry = createSection(feature)
                ?.on("click", this.onClickElement)
                .addTo(layer)

              const sectionMarker = await createSectionMarker(feature, null, {
                extrudeConfig,
              })
              sectionMarker?.on("click", this.handleClickElement)

              // Add marker to map based on type
              if (sectionMarker instanceof ui.UIMarker) {
                sectionMarker.addTo(this.map)
              } else {
                const sectionMarkerLayer = this.map.getLayer(
                  POI_MARKER_LAYER_NAME
                )
                sectionMarker?.addTo(sectionMarkerLayer)
              }

              if (id && sectionMarker) {
                const sectionMarkerId = getMarkerId(sectionMarker)
                elements[sectionMarkerId] = {
                  geometry: sectionMarker,
                  properties,
                  featureType,
                  feature,
                }
              }
            } else {
              const extrudeSection = createExtrudedSection(
                feature,
                this.threeLayer,
                option
              )

              if (!extrudeSection) break

              extrudeSection.on("click", this.handleClickElement)

              // Forgiving Click
              extrudeSection.on("touchstart", (e) => {
                this.#touchStartTarget = e.target
                this.#touchStartPoint = e.containerPoint
                this.map.on("touchend", this.handleMapTouchEnd)
              })

              object3ds.push(extrudeSection)
            }
            break
          }
          case "occupant": {
            switch (category) {
              // Create only marker if it is amenity occupant
              case "currencyexchange":
              case "donationcenter":
              case "postoffice":
                const markerFeature = {
                  ...feature,
                  geometry: feature.properties?.anchor?.geometry,
                }
                const marker3d = create3DOccupantAmenityMarker(
                  markerFeature,
                  this.threeLayer,
                  extrudeConfig
                )?.on("click", this.handleClickElement)
                object3ds.push(marker3d)
                break
              default: {
                // Main Location
                const { kiosk, anchor } = feature.properties
                const { unit } = anchor.properties
                let mainLocation = kiosk || unit || null

                // Get related locations excluding current ordinal
                const relatedLocations = [
                  ...feature.properties.units,
                  ...feature.properties.kiosks,
                ].filter((f) => f.properties.ordinal !== properties.ordinal)

                // Combine all locations to process in single loop
                const occupantLocations = [mainLocation, ...relatedLocations]
                const renderType = feature.properties.render_type

                occupantLocations.forEach((location, index) => {
                  const isMainLocation = index === 0

                  // Handle 3D elements (currently only ground labels)
                  if (renderType === "Label") {
                    const occupantGroundLabel = createOccupantGroundLabel(
                      feature,
                      location,
                      { textMarkerType, extrudeConfig },
                      this.threeLayer
                    )
                    // // Add data for ground label
                    occupantGroundLabel.properties = {
                      ...occupantGroundLabel.properties,
                      id: feature.id,
                      feature_type: "occupant",
                      render_type: "Label",
                      originalFeature: feature,
                    }

                    // Add 3D ground label to object3ds
                    if (occupantGroundLabel instanceof GroundLabel) {
                      occupantGroundLabel.on("click", this.handleClickElement)
                      occupantGroundLabel.addTo(this.threeLayer)
                      object3ds.push(occupantGroundLabel)
                      this.#groundObjects.push(occupantGroundLabel)
                    }
                  } else {
                    // Handle 2D markers (UIMarker or regular Marker)
                    const occupantMarker = createOccupant(feature, location, {
                      textMarkerType,
                      extrudeConfig,
                    })
                    /**
                     * !Temporary condition; can be removed later if we implement all markers (logo, logo + name, name) to UIMarker.
                     * UIMarker Can only be added to the map after rc.29
                     * https://github.com/maptalks/maptalks.js/releases/tag/v1.0.0-rc.29
                     */

                    // Add marker to map based on type
                    if (occupantMarker instanceof ui.UIMarker) {
                      occupantMarker.addTo(this.map)
                    } else {
                      occupantMarker?.on("click", this.handleClickElement)
                      occupantMarker?.addTo(layer)
                    }

                    if (isMainLocation) {
                      // Set main location add to elements (so it can be show/hide by ordinal)
                      geometry = occupantMarker
                    } else {
                      // Add related locations to elements (e.g., `occupant-123_unit-123_marker`) so they can be shown/hidden by ordinal
                      const duplexMarkerId = getMarkerId(occupantMarker)
                      elements[duplexMarkerId] = {
                        geometry: occupantMarker,
                        properties: location.properties,
                        featureType: "occupant",
                        feature,
                      }
                    }
                  }
                })
              }
            }
            break
          }
          case "fixture": {
            const models = await create3DFixture(feature, this.threeLayer)
            models.forEach((model) => {
              model.on("click", this.handleClickElement)
              object3ds.push(model)
              this.#glbObjects.push(model)
            })
            if (!featureExtrudeConfig) {
              geometry = createFixture(feature)?.addTo(layer)
            } else {
              const locatedLevel = feature?.properties?.level
              const levelExtrudeConfig = getExtrudeConfigByFeature(
                mergedExtrudeConfig,
                locatedLevel
              )
              const levelHeight = _.get(levelExtrudeConfig, "height", 0)
              const option = { ...featureExtrudeConfig, altitude: levelHeight }

              const extrudedFixture = createExtrudedUnit(
                feature,
                this.threeLayer,
                option
              )

              if (!extrudedFixture) break

              object3ds.push(extrudedFixture)
            }
            break
          }
          case "kiosk": {
            const models = await create3DKiosk(feature, this.threeLayer)
            models.forEach((model) => {
              model.on("click", this.handleClickElement)
              object3ds.push(model)
              this.#glbObjects.push(model)
            })
            const locatedLevel = feature?.properties?.level
            const levelExtrudeConfig = getExtrudeConfigByFeature(
              mergedExtrudeConfig,
              locatedLevel
            )
            const levelHeight = _.get(levelExtrudeConfig, "height", 0)
            const option = { ...featureExtrudeConfig, altitude: levelHeight }
            const hasAddedModel3d = models?.length > 0

            if (hasAddedModel3d || !featureExtrudeConfig) {
              geometry = createKiosk(feature)
                ?.on("click", this.handleClickElement)
                .addTo(layer)
            } else {
              const extrudedKiosk = createExtrudedKiosk(
                feature,
                this.threeLayer,
                option
              )

              if (!extrudedKiosk) break

              extrudedKiosk.on("click", this.handleClickElement)
              object3ds.push(extrudedKiosk)
            }

            break
          }
          case "footprint": {
            // Added 3D Footprint
            const objects = await create3DFootprint(
              feature,
              this.threeLayer,
              featureExtrudeConfig
            )
            objects.forEach((object) => {
              object.on("click", () => {
                const {
                  geometry: { coordinates },
                } = turfCenter(feature)
                const zoom = this.#defaultZoom + 1
                this.flyToAndZoomIn(coordinates, { zoom, pitch: 45 })
              })
              object3ds.push(object)
              this.#objects.push(object)
            })

            // Add Footprint Marker
            if (feature.properties.logo) {
              const footprintMarker = create3DBillboard(
                feature,
                this.threeLayer
              )
              object3ds.push(footprintMarker)
              this.#billboardObjects.push(footprintMarker)
            }

            break
          }
          default:
            break
        }
        if (!id) throw new Error(`Property is missing ID, ${id}`)
        elements[id] = { geometry, properties, featureType, feature }
      } catch (err) {
        console.warn(`Cannot create ${feature.id}: ${err.message}`)
      }
    }

    this.#groundLabels.forEach((label) => {
      const text = label.properties.name
      try {
        const groundLabel = create3DGroundLabel(label, this.threeLayer)
        object3ds.push(groundLabel)
        this.#groundObjects.push(groundLabel)
      } catch (error) {
        console.log("error creating ground label for ", text)
      }
    })

    this.#mapDecorations.forEach((decoration) => {
      const { id, geometry, properties } = decoration
      const geometryType = decoration?.geometry?.type
      try {
        switch (geometryType) {
          case "Point":
            const createdBillboard = create3DBillboard(
              decoration,
              this.threeLayer
            )
            this.#billboardObjects.push(createdBillboard)
            object3ds.push(createdBillboard)
            break
          default:
            const decorationLayer = this.map.getLayer(`base`)
            const createdDecoration = createDecoration(geometry, {
              id,
              ...properties,
            }).addTo(decorationLayer)
            elements[id] = {
              geometry: createdDecoration,
              properties,
              featureType: "decoration",
            }
            break
        }
      } catch (error) {
        console.log(
          "error creating decoration for ",
          decoration.properties.name
        )
      }
    })

    const globeModels = await create3DModels(
      [
        {
          // url: 'https://api-directory-qa.siampiwat.com/static/uploads/earth_864fc7049e.glb',
          url: "https://api-directory.siampiwat.com/static/uploads/lowpoly_earth_0b35beebce_a235d69530.glb",
          properties: {
            position: [100.53441498, 13.74697815],
            rotation: {
              x: 0.5 * Math.PI,
              y: 0.25 * Math.PI,
            },
            scale: [0.05, 0.05, 0.05],
            altitude: 8,
          },
        },
      ],
      [100.53441498, 13.74697815],
      { ordinal: 7 },
      this.threeLayer
    )

    const globe =
      Array.isArray(globeModels) && globeModels.length > 0
        ? globeModels[0]
        : null
    if (globe) {
      // Make Globe brighter
      globe.getObject3d().traverse((child) => {
        if (child.isMesh === true) {
          child.material.color.multiplyScalar(1.4) // brighten base texture
          child.material.emissive = new ThreeColor(0xffffff)
          child.material.emissiveIntensity = 0.3
        }
      })

      object3ds.push(globe)
      this.#glbObjects.push(globe)
      this.threeLayer.addMesh(globe)
    }

    // Initiate Canvas render
    this.render()
    this.#elements = elements
    this.#elementsLoaded = true
    this.#featuresInitted = true
    this.#object3ds = object3ds
    if (typeof this.onMapReady === "function") this.onMapReady()
    if (typeof this.onModelReady === "function") this.onModelReady()
  }

  #clearAllElementOnLayerByName = (layerName: string) => {
    const layer = this.map.getLayer(layerName)
    if (layer) layer.clear()
  }

  #showGeometriesByOrdinal(
    ordinals: number[],
    options: { altitude?: boolean } = {}
  ) {
    const { altitude = false } = options || {}
    const isMultiFloors =
      altitude || (Array.isArray(ordinals) && ordinals.length > 1)
    const hightestOrdinal = Math.max(...ordinals, 0)
    const altitudeByOrdinal = isMultiFloors
      ? computeAltitudeFromOrdinals(ordinals, MULTIORDINAL_HEIGHT_METER)
      : {}

    _(this.#elements)
      .filter((element) => !!_.get(element, "geometry"))
      .forEach(({ featureType, geometry, properties }) => {
        const elementOrdinal = properties?.ordinal
        //! NOTE: This condition ensures that only UI markers or Markers at the highest ordinal are displayed,
        const showMarker =
          isFinite(elementOrdinal) && hightestOrdinal === elementOrdinal
        const show =
          (Array.isArray(ordinals) &&
            (ordinals.length === 0 || ordinals.includes(elementOrdinal))) ||
          featureType === "venue"

        const layerName = _.get(
          LAYER_FEATURE_TYPE_OBJ,
          featureType,
          featureType
        )
        const markerLayerId = buildCompositeId([featureType], {
          separator: "-",
          suffix: SUFFIX_MARKER,
        })
        const markerLayerName = _.get(LAYER_FEATURE_TYPE_OBJ, markerLayerId)
        const layer = this.map.getLayer(layerName)
        const markerLayer = this.map.getLayer(markerLayerName)

        if (!layer && !markerLayer) return

        const altitudeFromOrdinal = isMultiFloors
          ? _.get(altitudeByOrdinal, `${elementOrdinal}.altitude`, 0)
          : 0

        if (geometry instanceof ui.UIMarker) {
          if (showMarker) {
            const defaultAltitude =
              geometry.options?.properties?.defaultAltitude || 0
            geometry.setAltitude(defaultAltitude + altitudeFromOrdinal)
          }
          return showMarker ? geometry?.addTo(this.map) : geometry.remove()
        }

        if (markerLayer && geometry instanceof Marker) {
          if (showMarker) {
            const defaultAltitude = geometry.properties?.defaultAltitude || 0
            geometry.setAltitude(defaultAltitude + altitudeFromOrdinal)
          }

          return showMarker
            ? markerLayer.addGeometry(geometry)
            : markerLayer.removeGeometry(geometry)
        }

        if (show) {
          const defaultAltitude = geometry.properties?.defaultAltitude || 0

          geometry.setAltitude(defaultAltitude + altitudeFromOrdinal)
        }
        show ? layer.addGeometry(geometry) : layer.removeGeometry(geometry)

        //! Prevent animating geometry from stopping after being removed and re-added to the layer.
        if (geometry._animPlayer instanceof animation.Player) {
          const animPlayer = geometry?._animPlayer
          show ? animPlayer.play() : animPlayer.cancel()
        }
      })
  }

  #showThreeObjectByOrdinal = (
    ordinals: number[],
    options: { altitude?: boolean } = {}
  ) => {
    const { altitude = false } = options || {}
    const threeLayer = this.threeLayer
    const objectsToAdd = []
    const objects = this.#object3ds || []

    objects?.forEach((baseObject) => {
      const objectOrdinal = _.get(baseObject, "properties.ordinal")
      const featureType = _.get(baseObject, "properties.feature_type")
      const isAdd = _.get(baseObject, "isAdd")
      const show =
        ordinals.includes(objectOrdinal) ||
        ALWAYS_VISIBLE_FEATURE_TYPES.includes(featureType)

      if (show) return objectsToAdd.push(baseObject)
      if (isAdd) return threeLayer.removeMesh(baseObject)
    })
    threeLayer.addMesh(objectsToAdd)

    // NOTE: Run setAltitude after addMesh to prevent "layer is undefined" errors.
    const isMultiFloors =
      altitude || (Array.isArray(ordinals) && ordinals.length > 1)

    const altitudeByOrdinal = isMultiFloors
      ? computeAltitudeFromOrdinals(ordinals, MULTIORDINAL_HEIGHT_METER)
      : {}

    objectsToAdd.forEach((baseObject) => {
      const objectOrdinal = _.get(baseObject, "properties.ordinal")
      const altitudeFromOrdinal = isMultiFloors
        ? _.get(altitudeByOrdinal, `${objectOrdinal}.altitude`, 0)
        : 0
      baseObject.setAltitude(
        baseObject.getOptions().defaultAltitude + altitudeFromOrdinal
      )
    })
  }

  /**
   * Change Level & animate to path / geometry / view / etc.
   * ================================== */
  changeLevelByOrdinal(
    ordinals: number | number[],
    options: { altitude?: boolean } = {}
  ): void {
    const { altitude = false } = options || {}
    if (!this.#elementsLoaded) return
    const targetOrdinals = (
      Array.isArray(ordinals) ? ordinals : [ordinals]
    ).filter((ordinal) => isFinite(ordinal))
    this.#showGeometriesByOrdinal(targetOrdinals, { altitude }) // show / hide geometry on the ordinal
    this.#showThreeObjectByOrdinal(targetOrdinals, { altitude })
  }

  #animateflyTo(viewOptions = {}, options = {}, callbackOptions = {}) {
    const { start, end } = {
      start: (frame) => {},
      end: (frame) => {},
      ...callbackOptions,
    }

    this.map.flyTo(viewOptions, options, (frame) => {
      if (frame.state.playState === "running" && frame.state.progress === 0)
        start(frame)
      if (frame.state.playState === "finished") end(frame)
    })
  }

  getBearing = () => this.map.getBearing()
  getPitch = () => this.map.getPitch()
  getZoom = () => this.map.getZoom()
  setBearing = (bearing) => this.map.setBearing(bearing)

  getFeatureExtent = (feature, scaleFactor = 1) => {
    const featureBbox = bbox(scale(bboxPolygon(bbox(feature)), scaleFactor))

    return new Extent(...featureBbox)
  }

  getExtentCenter = (extent: Extent) => {
    return extent.getCenter()
  }

  getExtentZoom = (
    extent: Extent,
    options: { isFraction?: boolean; padding?: MapPaddingType } = {
      isFraction: false,
      padding: {
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
      },
    }
  ) => {
    const { isFraction = false, ...restOptions } = options
    return this.map.getFitZoom(extent, isFraction, restOptions)
  }

  findVenueInView = (): { venueId: string; distance: number } => {
    const mapCenter = this.map.getCenter()
    const result = this.#venues.reduce<{
      venueId: string
      distance: number
    } | null>((closest, venue) => {
      const { display_point: displayPoint } = venue.properties
      const distance = turfDistance(displayPoint, [mapCenter.x, mapCenter.y])
      if (!closest || distance < closest.distance) {
        return { venueId: venue.id, distance }
      }
      return closest
    }, null)
    return result
  }

  flyTo = (
    center,
    options: MapAnimationOptionsType = {
      zoom: this.#defaultZoom,
      pitch: 60,
      duration: 600,
      delay: 2000,
      ease: "out",
      bearing: this.getBearing(),
    }
  ) => {
    const {
      zoom = this.#defaultZoom,
      pitch = 60,
      duration = 600,
      delay = 2000,
      ease = "out",
      bearing = this.getBearing(),
    } = options
    this.#animateflyTo(
      {
        center,
        zoom,
        pitch,
        bearing,
      },
      { delay, duration, ease }
    )
  }
  getLineStringBearing = (feature) => {
    const { geometry } = feature
    const path = new LineString(geometry.coordinates)
    return getBearingBetweenPoints(
      path.getFirstCoordinate(),
      path.getLastCoordinate()
    )
  }

  //map animation
  addAnimations(animation: { id: string; callback: () => void }) {
    this.#animationsToRun.push(animation)
  }

  removeAnimationById(id: string) {
    this.#animationsToRun = this.#animationsToRun.filter(
      (animation) => animation.id !== id
    )
  }

  clearAnimations() {
    this.#animationsToRun = []
  }
  // Subject to be removed
  flyToAndZoomIn = (
    centerPoint,
    options: MapAnimationOptionsType = {
      zoom: this.#defaultZoom + 3,
      pitch: 60,
      duration: 600,
      delay: 2000,
      ease: "out",
    }
  ) => {
    const {
      zoom = this.#defaultZoom + 3,
      pitch = 60,
      duration = 600,
      delay = 2000,
      ease = "out",
    } = options
    this.#animateflyTo(
      {
        center: centerPoint,
        zoom,
        pitch,
      },
      { delay, duration, ease }
    )
  }

  /**
   * Hilighting Elements
   * ========================================= */
  // TODO: To consider if we should use setter `set hilightElementIds` instead?
  setHighlightElementIds(
    targetElementIds,
    options: ISetHighlightElementIdsOptions = {}
  ) {
    const highlight3DOptions = _.merge(
      {},
      DEFAULT_HIGHLIGHT_OPTIONS,
      _.get(options, "highlight3DOptions", {})
    )
    const highlight2DOptions = _.merge(
      {},
      DEFAULT_SET_HIGHLIGHT_2DELEMENT_IDS_OPTIONS,
      _.get(options, "highlight2DOptions", {})
    )

    this.setHighlightedObject(targetElementIds, highlight3DOptions)
    return this.setHighlight2DElementIds(targetElementIds, highlight2DOptions)
  }
  setHighlight2DElementIds(targetElementIds, options = {}) {
    const {
      defaultMarker,
      symbolSet,
      markerSymbolSet,
      isMultiFloors,
      ordinals,
    } = _.merge({}, DEFAULT_SET_HIGHLIGHT_2DELEMENT_IDS_OPTIONS, options)
    const altitudeByOrdinal = isMultiFloors
      ? computeAltitudeFromOrdinals(ordinals, MULTIORDINAL_HEIGHT_METER)
      : {}
    const {
      createMarker,
      createHighlightOccupantMarker,
      createHighlightSectionMarker,
      getElementSymbol,
      getHilighPolygonalSymbol,
      getHighlightMarkerSymbol,
    } = this.#styler
    const extrudeConfig = _.get(this.#mapConfig, "extrude")

    /**
     * Hilight new elements
     */
    const elementToHilights = targetElementIds
      .map(
        (elemId) =>
          this.#elements[elemId] ||
          this.#elements[`${LAST_USER_LOCATION_ELEMENT_ID_PREFIX}${elemId}`]
      )
      .filter((elem) => elem)

    elementToHilights.forEach(async (elem) => {
      const { feature, geometry } = elem
      if (!geometry || !feature) return

      const hilightLayer = this.map.getLayer(HIGHLIGHT_LAYER_NAME)
      if (!hilightLayer) return

      const venueReference = _.get(
        feature,
        "properties.venue.properties.reference"
      ) // One Siam
      const defaultSymbol = getHilighPolygonalSymbol(
        geometry.type,
        venueReference
      )
      const definedMarkerSymbol = markerSymbolSet
        ? getElementSymbol(markerSymbolSet)
        : null
      const definedSymbol = symbolSet ? getElementSymbol(symbolSet) : null
      const symbol = _.isEmpty(definedSymbol) ? defaultSymbol : definedSymbol

      switch (geometry.type) {
        case "MultiPolygon":
        case "Polygon": {
          geometry?.updateSymbol(symbol)
          break
        }
        default:
          break
      }

      switch (feature.feature_type) {
        case "amenity":
          const highlightedAmenityMarker =
            definedMarkerSymbol || getHighlightMarkerSymbol()
          geometry?.updateSymbol(highlightedAmenityMarker)
          break
        case "occupant": {
          switch (feature.properties.category) {
            case "currencyexchange":
            case "donationcenter":
            case "postoffice":
              const highlightedAmenityMarker =
                definedMarkerSymbol || getHighlightMarkerSymbol()
              geometry?.updateSymbol(highlightedAmenityMarker)
              break
            default:
              if (feature.properties.render_type === "Logo") {
                this.hideRelatedHighlightElementById(geometry?.properties?.id)
              }
              createHighlightOccupantMarker(feature, {
                extrudeConfig,
                symbol: definedMarkerSymbol,
              })
                .on("click", this.handleClickElement)
                .addTo(hilightLayer)
              break
          }
          break
        }
        case "section": {
          if (feature.properties.render_type === "Logo") {
            this.hideRelatedHighlightElementById(geometry?.properties?.id)
          }
          const altitudeFromOrdinal = isMultiFloors
            ? _.get(
                altitudeByOrdinal,
                `${getFeatureOrdinal(feature)}.altitude`,
                0
              )
            : 0
          const marker = await createHighlightSectionMarker(feature, {
            extrudeConfig,
            symbol: definedMarkerSymbol,
            altitude: altitudeFromOrdinal,
            altitudeModifier: altitudeFromOrdinal,
          })

          marker.on("click", this.handleClickElement).addTo(hilightLayer)
          break
        }
        case "opening":
          break
        default:
          if (defaultMarker) createMarker(feature).addTo(hilightLayer)
          break
      }

      /* Hide the occupant marker before display the highlight marker */
      // const occupantId = IndexedOccupantMarkers[feature.id]
      // if (occupantId) {
      //   hideOccupantMarker(occupantId)
      // }
    })

    // Store new highlightIds to prev ref.
    this.#highlightElementIds = targetElementIds
    if (elementToHilights.length === 0) return
    return featureCollection(
      elementToHilights.map(({ feature }) => {
        const { geometry } = feature
        if (feature.feature_type === "occupant")
          return turfFeature(feature?.properties?.anchor?.geometry)
        return turfFeature(geometry)
      })
    )
  }

  clearHighlightElements() {
    /**
     * Clear previous highlights
     */
    this.#clearAllElementOnLayerByName(HIGHLIGHT_LAYER_NAME)
    //Return geometry to defaultSymbol

    // TODO: Consider ensuring that section or other separating marker elements keep their original marker ID in properties.
    const elementIds = [
      ...(this.#highlightElementIds || []),
      ...(this.#relatedHighlightHiddenElementIds || []),
    ]
    _(elementIds)
      .map((elemId) => {
        const feature = this.#elements[elemId]?.feature
        const geometry = this.#elements[elemId]?.geometry

        if (!geometry) return null

        return {
          feature,
          geometry,
        }
      })
      .compact()
      .forEach(({ geometry, feature }) => {
        if (geometry instanceof ui.UIMarker) return

        if (geometry instanceof Marker) {
          this.showGeometryByElementId(geometry.properties.id)
          return
        }

        try {
          const defaultSymbol = geometry.options.defaultSymbol
          geometry.updateSymbol(defaultSymbol)
        } catch (err) {
          console.log(
            `error cannot return to defaultSymbol, check if "defaultSymbol" exists in element creation function`
          )
        }
      })
    this.#highlightElementIds = []
    this.#relatedHighlightHiddenElementIds = []
  }

  setHighlightedObject(targetObjectIds, options = {}) {
    const { symbolSet, markerSymbolSet } = _.merge(
      {},
      DEFAULT_HIGHLIGHT_OPTIONS,
      options
    )
    const {
      getElementSymbol,
      getHilighPolygonalSymbol,
      createHighlight2DAmenityMarkerFrom3DMarker,
      createHighlightOccupantMarker,
    } = this.#styler
    const objects = this.threeLayer?.getBaseObjects()
    const objectsToHighlight = objects.filter(({ properties }) =>
      targetObjectIds.includes(properties?.id)
    )

    const amenityHighlightMode = _.get(
      this.#mapConfig,
      "amenity_highlight_mode",
      ""
    )

    const extrudeConfig = _.get(this.#mapConfig, "extrude")

    objectsToHighlight.forEach((obj) => {
      const venueReference = _.get(obj, "properties.venueReference") // One Siam
      const defaultSymbol = getHilighPolygonalSymbol("Polygon", venueReference)
      const definedMarkerSymbol = markerSymbolSet
        ? getElementSymbol(markerSymbolSet)
        : null
      const targetSymbol = symbolSet ? getElementSymbol(symbolSet) : null
      const { polygonFill: color } = _.isEmpty(targetSymbol)
        ? defaultSymbol
        : targetSymbol

      //highlight extruded polygon
      if (obj.type === "ExtrudePolygon") {
        const newController = createHighlighExtrudeObjectController(obj, {
          color,
        })
        newController.start()
        this.#highlightObjectControllers.push(newController)
      }
      //highlight amenity marker
      if (obj instanceof SpriteMarker) {
        if (amenityHighlightMode === "2DMarker") {
          const highlight2DLayer = this.map.getLayer(HIGHLIGHT_LAYER_NAME)
          const extrudeConfig = _.get(this.#mapConfig, "extrude")
          obj.hide()
          const { properties: featureProperties } = obj
          createHighlight2DAmenityMarkerFrom3DMarker(
            featureProperties,
            extrudeConfig
          )
            .on("click", this.handleClickElement)
            .addTo(highlight2DLayer)
        } else {
          obj.highlight()
        }
      }

      if (obj instanceof Billboard) {
        const newController = createHighlighBillboardController(obj)
        newController.start()
        this.#highlightObjectControllers.push(newController)
      }

      // Handle case occupant groundLabel
      if (obj instanceof GroundLabel) {
        const highlight2DLayer = this.map.getLayer(HIGHLIGHT_LAYER_NAME)
        const { properties: objProperties } = obj

        //Find Occupant that has render type is Label
        const occupantFeature = this.#features.find(
          (feature) =>
            feature.id === objProperties.id &&
            feature.feature_type === "occupant" &&
            feature.properties.render_type === "Label"
        )

        if (occupantFeature) {
          //create 2D highlight occupant marker
          const highlightMarker = createHighlightOccupantMarker(
            occupantFeature,
            {
              extrudeConfig,
              symbol: definedMarkerSymbol,
            }
          )

          // if 2D occupant marker created add to highlight layer
          if (highlightMarker) {
            highlightMarker
              .on("click", this.handleClickElement)
              .addTo(highlight2DLayer)
          }
        }
      }
    })

    this.#highlightObjectIds = targetObjectIds
  }

  clearHighlightObject() {
    this.#highlightObjectControllers.forEach((controller) => {
      if (_.isFunction(controller?.clear)) controller.clear()
    })
    this.#highlightObjectIds.forEach((objIds) => {
      const objects = this.threeLayer?.getBaseObjects()
      const objectToResetHighlight = objects.find(({ properties }) =>
        objIds.includes(properties?.id)
      )
      if (objectToResetHighlight instanceof SpriteMarker) {
        objectToResetHighlight.show()
        objectToResetHighlight.removeHighlight()
      }
    })
    // Clear the highlight object controllers
    this.#highlightObjectControllers = []
    this.#highlightObjectIds = []
  }

  hideObjectByIds(targetObjectIds, options: { mode: "include" | "exclude" }) {
    const { mode = "include" } = options || {}
    const isIncludeMode = mode === "include"
    const objects = this.threeLayer?.getBaseObjects()
    const objectsToHide = objects.filter((object) => {
      if (!object?.properties?.id) return false

      const matchedObject = targetObjectIds.includes(object?.properties?.id)

      return isIncludeMode ? matchedObject : !matchedObject
    })

    objectsToHide.forEach((object) => {
      if (object instanceof BaseObject) {
        object.hide()
        this.#hiddenObjectIds.push(object?.properties?.id)
      }
    })
  }

  setOpacityObjectByIds(
    targetObjectIds,
    opacity = 1,
    options: { mode: "include" | "exclude" }
  ) {
    const { mode = "include" } = options || {}
    const isIncludeMode = mode === "include"
    const objects = this.threeLayer?.getBaseObjects()
    const targetObjects = objects.filter((object) => {
      if (!object?.properties?.id) return false

      const matchedObject = targetObjectIds.includes(object?.properties?.id)

      return isIncludeMode ? matchedObject : !matchedObject
    })

    targetObjects.forEach((object) => {
      object.getObject3d().traverse((child) => {
        //@ts-ignore
        if (child.isMesh === true && child.material) {
          //@ts-ignore
          child.material.opacity = opacity
        }
      })
    })
  }

  undoHideObjects() {
    this.#hiddenObjectIds.forEach((objId) => {
      const objects = this.threeLayer?.getBaseObjects()
      const objectToUndo = objects.find(({ properties }) =>
        objId.includes(properties?.id)
      )
      if (objectToUndo instanceof BaseObject) objectToUndo.show()
    })

    this.#hiddenObjectIds = []
  }

  /**
   * User Location
   ****************************/
  // TODO: To consider if we should use setter `set userLocation` instead?

  addUserLocation(value) {
    const { createUserLocationMarker } = this.#styler

    this.#userLocationGeometry = value

    const { properties, feature_type } = value

    // If UserLocationGeometry not exists, create one.

    const markerLayer = this.map.getLayer(USER_LOCATION_LAYER_NAME)

    if (!this.#userLocationElement?.geometry && markerLayer) {
      const geometry = createUserLocationMarker(value)
      geometry.addTo(markerLayer)

      // Add marker to this.#elements
      const element = {
        geometry,
        properties,
        featureType: feature_type,
        feature: value,
      }
      this.#elements[USER_LOCATION_ELEMENT_ID] = element
      this.#userLocationElement = element

      this.updateUserLocationSymbolByLocale(this.locale)
    }
  }

  updateUserLocationSymbolByLocale(locale: string) {
    const userLocationGeometry = _.get(
      this.#elements,
      `${USER_LOCATION_ELEMENT_ID}.geometry`
    )

    if (!userLocationGeometry) return

    const currentSymbol = userLocationGeometry.getSymbol()

    /**
     * Localized user location marker example.
     * Supports locale-based marker files.
     *
     * Example:
     * {
     *   "markerFile": "logo_url", // Default marker
     *   "locale_symbol": { "th": { "markerFile": "logo_url" }, "default": { "markerFile": "logo_url" } }
     * }
     */
    const localeSymbolToUpdate = currentSymbol.map((symbol) => {
      const localeSymbol =
        _.get(symbol, `${LOCALE_SYMBOL_KEY}.${locale}`) ||
        _.get(symbol, `${LOCALE_SYMBOL_KEY}.default`)
      if (!_.isPlainObject(localeSymbol)) return symbol

      return {
        ...symbol,
        ...localeSymbol,
      }
    })
    userLocationGeometry?.updateSymbol(localeSymbolToUpdate)
  }

  removeUserLocation() {
    this.#userLocationGeometry = null
    if (this.#userLocationElement) {
      const { geometry } = this.#userLocationElement
      if (geometry) geometry.remove()

      this.#userLocationElement = null
      this.#elements[USER_LOCATION_ELEMENT_ID] = null
    }
  }

  showUserLocationMarker() {
    this.#userLocationElement?.geometry.show()
  }

  hideUserLocationMarker() {
    this.#userLocationElement?.geometry.hide()
  }

  addLastUserLocation(value) {
    const newLastLocationId = value?.id
    // If the last location ID is undefined or exists, no new marker will be added.
    const isExists =
      this.#lastUserLocationGeometries.findIndex(
        ({ feature }) => feature?.id === newLastLocationId
      ) >= 0

    if (!newLastLocationId || isExists) return

    const { createLastUserLocationMarker } = this.#styler

    this.#lastUserLocationGeometries = [
      ...this.#lastUserLocationGeometries,
      value,
    ]

    const { properties, feature_type, id } = value

    const markerLayer = this.map.getLayer(USER_LOCATION_LAYER_NAME)

    const geometry = createLastUserLocationMarker(value)
    if (!geometry) return
    geometry.addTo(markerLayer)

    // Add marker to this.#elements
    const element = {
      geometry,
      properties,
      featureType: feature_type,
      feature: value,
    }
    this.#elements[`${LAST_USER_LOCATION_ELEMENT_ID_PREFIX}${id}`] = element
    this.#lastUserLocationElements = [
      ...this.#lastUserLocationElements,
      element,
    ]
  }

  removeLastUserLocation() {
    this.#lastUserLocationGeometries = []
    if (
      this.#lastUserLocationElements &&
      this.#lastUserLocationElements.length > 0
    ) {
      for (const lastUserLocationElem of this.#lastUserLocationElements) {
        const { geometry, properties = {} } = lastUserLocationElem
        if (geometry) {
          geometry.remove()
          delete this.#elements[`last_user_location-${properties?.id}`]
        }
      }
      this.#lastUserLocationElements = []
    }
  }

  hideLastUserLocationMarker() {
    for (const lastUserLocationElem of this.#lastUserLocationElements) {
      const { geometry } = lastUserLocationElem
      if (geometry) geometry.hide()
    }
  }

  /**
   * END of User Location
   ****************************/

  showGeometryByElementId = (elementId): void => {
    const geometry: Geometry | undefined = _.get(
      this.#elements,
      `${elementId}.geometry`
    )
    if (geometry) geometry.show()
  }

  hideRelatedHighlightElementById = (elementId): void => {
    const geometry: Geometry = _.get(this.#elements, `${elementId}.geometry`)
    if (geometry) {
      geometry.hide()
      if (Array.isArray(this.#relatedHighlightHiddenElementIds)) {
        this.#relatedHighlightHiddenElementIds.push(elementId)
      }
    }
  }

  hideRelatedHighlightElementByIds = (elementIds = []): void => {
    elementIds.forEach((id) => this.hideRelatedHighlightElementById(id))
  }

  hideElementById = (elementId): void => {
    const geometry: Geometry = _.get(this.#elements, `${elementId}.geometry`)
    if (geometry) {
      geometry.hide()
      if (Array.isArray(this.#relatedHighlightHiddenElementIds)) {
        this.#relatedHighlightHiddenElementIds.push(elementId)
      }
    }
  }

  hideElementByIds = (elementIds = []): void => {
    elementIds.forEach((id) => this.hideElementById(id))
  }

  undoHiddenElements = () => {
    this.#hiddenElementIds.forEach((elementId) => {
      const geometry = _.get(this.#elements, `${elementId}.geometry`)
      if (!geometry) return
      geometry?.show()
    })
    this.#hiddenElementIds = []
  }

  setSpriteMarkersOpacity = (opacity: number = 1): void => {
    const spriteMarkerObjects = this.#spriteMarkerObjects
    spriteMarkerObjects?.forEach((baseObject) => {
      baseObject.getObject3d().traverse((child) => {
        if (child.isLine || child.isSprite) {
          child.material.opacity = opacity
        }
      })
    })
  }

  setFeatureObject3DsOpacity = (opacity: number = 1): void => {
    const objects = this.#object3ds
    objects?.forEach((baseObject) => {
      if (baseObject instanceof NavigationPath) return
      baseObject.getObject3d().traverse((child) => {
        if (child.isMesh === true) {
          child.material.opacity = opacity
        }
      })
    })
  }

  /*
   * Enable or disable the fading effect based on the zoom level for all layers except the Venue Model.
   * This function applies when extruded units exceed the edges of the Venue Model while zooming out.
   * It can be enabled only when the Venue Model is present.
   */
  disableLayersFadingOnZoom = (): void => {
    this.#isLayersFadingOnZoom = false
  }

  enableLayersFadingOnZoom = (): void => {
    this.#isLayersFadingOnZoom = this.#venueObjects.length !== 0
  }

  /**
   * Navigation
   ****************************/
  combineNearbyLineStrings(
    lineStrings: Feature<LineStringType, Properties>[],
    options: {
      properties?: Properties
      distance?: number
    } = { properties: {}, distance: 0.0003 }
    // 0.0003 = 30cm
  ) {
    const { properties = {}, distance = 0.0003 } = options || {}
    const combinedLineStrings = []
    const accLine = []
    if (lineStrings.length === 1) return lineStrings

    for (let i = 0; i < lineStrings.length; i++) {
      const line = lineStrings[i]
      const coords = line.geometry.coordinates
      const prevLine = lineStrings[i - 1]
      const firstCoord = _.first(coords)
      const isFirstLine = i === 0

      if (isFirstLine) {
        accLine.push(...coords)
        continue
      }

      const prevLastCoord = _.last(prevLine.geometry.coordinates)
      const isNearby =
        turfDistance(point(firstCoord), point(prevLastCoord)) < distance

      if (!isNearby) {
        const remainingLines = lineStrings.slice(i)
        const res = this.combineNearbyLineStrings(remainingLines, {
          properties,
        })
        combinedLineStrings.push(...res)
        break
      }
      accLine.push(...coords)
    }
    combinedLineStrings.push(lineString(accLine, properties))
    return combinedLineStrings
  }

  createNavigationGeometries = (stepGeometries, destinationFeature) => {
    const {
      createOriginMarker,
      createDestinationPinMarker,
      createDestinationLogoMarker,
      create3DStepPath,
    } = this.#styler
    const routeMarkerLayer = this.map.getLayer(HIGHLIGHT_LAYER_NAME)

    const linesByOrdinal = _(stepGeometries)
      .filter(({ geometry }) => geometry.type === "LineString")
      .groupBy("properties.ordinal")
      .value()

    const joinedLines = _(linesByOrdinal).reduce((acc, lines, key) => {
      const joined = this.combineNearbyLineStrings(lines, {
        properties: { ordinal: +key },
      })
      return [...acc, ...joined]
    }, [])

    joinedLines.forEach((line, index) => {
      try {
        const navPath: NavigationPath = create3DStepPath(line, this.threeLayer)
        this.threeLayer.addMesh([navPath])
        this.#navigationGeometries[`line-${index}`] = navPath
        this.#object3ds.push(navPath)
      } catch (err) {
        console.log(err)
      }
    })

    stepGeometries.forEach((stepGeometry) => {
      const { geometry, properties, feature_type = null } = stepGeometry
      let stepElement
      try {
        switch (geometry.type) {
          // Create a destination marker and route path
          case "Point":
            switch (feature_type) {
              case "origin-marker":
                stepElement =
                  createOriginMarker(stepGeometry).addTo(routeMarkerLayer)
                break
              case "destination-marker":
                const extrudeConfig = _.get(this.#mapConfig, "extrude")
                if (destinationFeature.feature_type === "occupant") {
                  const stepId = _.get(stepGeometry, "id")
                  // !Update step ID to occupant destination feature ID to prevent unexpected clearing of occupant element from map
                  const normalizedDestinationFeature = {
                    ...destinationFeature,
                    id: stepId,
                  }
                  stepElement = createDestinationLogoMarker(
                    normalizedDestinationFeature,
                    { extrudeConfig }
                  ).addTo(routeMarkerLayer)
                  break
                }
                stepElement = createDestinationPinMarker(destinationFeature, {
                  extrudeConfig,
                }).addTo(routeMarkerLayer)
                break
              default:
                this.#navigationGeometries[stepGeometry.id] = stepElement
                break
            }
            break
          default:
            break
        }
        if (stepElement) {
          const { id, feature_type: featureType } = stepGeometry
          // Add tot Elements object
          this.#elements[id] = {
            geometry: stepElement,
            featureType,
            properties: { ...stepGeometry, ...properties },
          }
          //
        }
      } catch (err) {
        // console.log(err, {
        //   index: index,
        //   stepGeometries,
        // })
        // throw err;
        console.log(err)
      }
    })
  }

  createOverviewStepPathByOrdinal = (stepGeometries, viewingOrdinal) => {
    const { createLineStringFromGeometries } = this.#styler
    const initialStepGeometries = stepGeometries
      .filter(({ properties }) => properties.ordinal === viewingOrdinal)
      .map(({ geometry }) => geometry)
    return createLineStringFromGeometries(initialStepGeometries)
  }

  clearNavigationGeometries(): void {
    // Remove origin and destination markers from the highlight layer
    const routeMarkerLayer = this.map.getLayer(HIGHLIGHT_LAYER_NAME)
    const originMarkerGeometry = _.get(
      this.#elements,
      `${ORIGIN_MARKER_ID}.geometry`
    )
    const destinationMarkerGeometry = _.get(
      this.#elements,
      `${DESTINATION_MARKER_ID}.geometry`
    )
    const geometriesToRemove = _.compact([
      originMarkerGeometry,
      destinationMarkerGeometry,
    ])

    routeMarkerLayer.removeGeometry(geometriesToRemove)

    this.#elements[ORIGIN_MARKER_ID] = null
    this.#elements[DESTINATION_MARKER_ID] = null

    // Filter out NavigationPath objects from the global this.#object3ds list
    this.#object3ds = this.#object3ds.filter(
      (obj) => !(obj instanceof NavigationPath)
    )

    // Remove related navigation geometry objects
    const objects = this.#navigationGeometries || {}
    _.forEach(objects, (obj) => {
      if (!obj) return
      this.#navigationGeometries[obj.properties.id] = null
      obj.remove()
    })
  }
  /**
   * END of Navigation
   ****************************/

  /**
   * hide/show venue 3dmodel
   **/

  hideVenueObjects = () => {
    this.showVenueObject = false
  }
  showVenueObjects = () => {
    this.showVenueObject = true
  }

  /**
   * Other functions
   */

  enableClick = () => this.map.config({ geometryEvents: true })
  disableClick = () => this.map.config({ geometryEvents: false })

  freeze = () => this.map.config({ zoomable: false, draggable: false })
  unfreeze = () => this.map.config({ zoomable: true, draggable: true })

  /**
   * render (frame)
   */
  getTargetViewCenter = (
    targetView,
    options = { offset: { top: 0, left: 0, right: 0, bottom: 0 } }
  ) => {
    // Extend the input targetView with default values from the current view if not provided
    const map = this.map
    const { offset } = options
    const { top = 0, left = 0, right = 0, bottom = 0 } = offset
    // Temporarily set the map to the target view to calculate the extent.
    // This approach requires that the map view can be adjusted without triggering a visible change.
    const originalState = {
      bearing: map.getBearing(),
      center: map.getCenter(),
      pitch: map.getPitch(),
      zoom: map.getZoom(),
    }

    const finalView = {
      bearing: _.isNil(targetView.bearing)
        ? map.getBearing()
        : targetView.bearing,
      center: _.isNil(targetView.center) ? map.getCenter() : targetView.center,
      pitch: _.isNil(targetView.pitch) ? map.getPitch() : targetView.pitch,
      zoom: _.isNil(targetView.zoom) ? map.getZoom() : targetView.zoom,
    }

    map.setView(finalView)

    // Calculate the visible extent at this state
    const projectedTargetCenter = map
      .coordinateToContainerPoint(finalView.center)
      .add(right / 2 - left / 2, bottom / 2 - top / 2)
    const adjustedTargetCenter = map.containerPointToCoordinate(
      projectedTargetCenter
    )
    // Reset the map to its original state to avoid any visible changes
    map.setView(originalState)
    return adjustedTargetCenter
  }

  setMaxExtent(extent: Extent) {
    return this.map.setMaxExtent(extent)
  }

  render() {
    // TODO: Update User Location marker position
    const view = this.map.getView()
    const zoomLevel = this.getZoom()
    const currBearing = view.bearing
    this.threeLayer._needsUpdate = !this.threeLayer._needsUpdate
    if (this.threeLayer._needsUpdate) {
      // @ts-ignore
      this.threeLayer.redraw()
    }

    if (this.threeLayer) {
      // zoom = 19, opacity = 0
      // zoom = 18.5, opacity = 1
      const objectOpacity = _.clamp(38 - 2 * this.#zoomLevel, 0, 1)
      this.#objects.forEach((object) => {
        object.getObject3d().traverse((child) => {
          if (child.isMesh) child.material.opacity = objectOpacity
          //TODO: recheck this line if it actually works
          /** set Mesh visible = false when opacity is 0
           * and true when opacity > 0
           */
          child.visible = !!objectOpacity
        })
        object.getObject3d().visible = !!objectOpacity
      })

      if (this.#billboardObjects) {
        this.#billboardObjects.forEach((object) => {
          const objectScale = _.clamp(20 - 1 * this.#zoomLevel, 1, 1.05)
          object.getObject3d().scale.set(objectScale, objectScale, 1)
        })
      }

      /*
       * The layer appears while zooming in and disappears when zooming out,
       * fully vanishing when the Venue Model is visible (opacity: 1).
       */
      if (this.#isLayersFadingOnZoom) {
        const layerOpacity = _.clamp(1 - objectOpacity, 0, 1)
        // Fading 2D layers.
        LAYERS.forEach((layerKey) => {
          const layer = this.map.getLayer(layerKey)
          if (layer) layer.setOpacity(layerOpacity)
        })

        // Fading 3D units, such as extruded units.
        this.setFeatureObject3DsOpacity(layerOpacity)

        // Fading sprite markers, such as billboards and amenity markers.
        this.setSpriteMarkersOpacity(layerOpacity)
      }

      //! Limiting 3D model opacity is currently available only for the One Siam project
      const based3DModelOpacity = _.clamp(38 - 2 * zoomLevel, 0, 1)
      const venueObjectOpacity = _.clamp(based3DModelOpacity, 0, 0.75)
      this.#venueObjects.forEach((object) => {
        object.getObject3d().traverse((child) => {
          if (child.isMesh) child.material.opacity = venueObjectOpacity
          if (child.isSprite) child.material.opacity = based3DModelOpacity
          /** set Mesh visible = false when opacity is 0
           * and true when opacity > 0
           */
          child.visible = this.showVenueObject && venueObjectOpacity > 0.4
        })
      })

      // Update bearing to each GroundLabel to determine if text should flip or not
      this.#groundObjects.forEach((gLabel) => {
        gLabel.bearing = currBearing
      })
    }

    this.#animationsToRun.forEach(({ callback }) => callback(this))

    //update TWEEN.js animaton
    TWEEN.update()

    requestAnimationFrame(this.render.bind(this))
  }
}
